import React, { useState, useEffect, useMemo } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { sv } from 'date-fns/locale/sv';
import 'react-datepicker/dist/react-datepicker.css';
registerLocale('sv', sv);
import {
  PieChart, Pie, Cell, Legend, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer
} from 'recharts';
import { fetchWithAuth, getUserInfoById } from '../services/api';
import { useIsAuthorized, useMonthlyReportNames, useImpersonatedUserId, useUserInfo, useStation } from '../contexts/GlobalStateContext';
import BattenburgLoader from '../common/BattenburgLoader';
import StationValjare from '../common/StationValjare';
import { toast } from 'react-toastify';

// ─────────────────────────────────────────────
// Nyckeltal
// ─────────────────────────────────────────────
const Nyckeltal = ({ label, value, color = 'tw-bg-blue-50 tw-border-blue-200' }) => (
  <div className={`tw-border tw-rounded tw-p-3 ${color}`}>
    <div className="tw-text-xs tw-text-gray-600 tw-mb-1">{label}</div>
    <div className="tw-text-xl tw-font-bold">{value}</div>
  </div>
);

// ─────────────────────────────────────────────
// Statistik-panel (delas av VakansTab)
const RightTable = ({ data }) => {
  const s = data?.manadsStatistik;
  const enheter = data?.enhetStatistik ?? [];

  const { stationSpan, enhetSpan, enhetTotals } = useMemo(() => {
    const stationSpan = new Array(enheter.length).fill(0);
    const enhetSpan = new Array(enheter.length).fill(0);

    for (let i = 0; i < enheter.length; i++) {
      // Station: ny cell när station byter
      if (i === 0 || enheter[i].station !== enheter[i - 1].station) {
        let span = 1;
        while (i + span < enheter.length && enheter[i + span].station === enheter[i].station) span++;
        stationSpan[i] = span;
      }
      // Enhet: ny cell när station ELLER enhet byter
      if (i === 0 || enheter[i].station !== enheter[i - 1].station || enheter[i].enhetsNummer !== enheter[i - 1].enhetsNummer) {
        let span = 1;
        while (i + span < enheter.length
          && enheter[i + span].station === enheter[i].station
          && enheter[i + span].enhetsNummer === enheter[i].enhetsNummer) span++;
        enhetSpan[i] = span;
      }
    }

    // Nyckel på station|enhet för att undvika kollision mellan stationer
    const enhetTotals = {};
    enheter.forEach(e => {
      const key = `${e.station}|${e.enhetsNummer}`;
      if (!enhetTotals[key]) enhetTotals[key] = { totalBesattningar: 0, totalVakanser: 0 };
      enhetTotals[key].totalBesattningar += e.totalBesattningar ?? 0;
      enhetTotals[key].totalVakanser += e.totalVakanser ?? 0;
    });

    return { stationSpan, enhetSpan, enhetTotals };
  }, [enheter]);

  if (!s) return null;

  const cell = 'tw-border tw-border-gray-300 tw-px-2 tw-py-1';
  return (
    <div className="tw-space-y-4 tw-flex-shrink-0">
      <div>
        <h4 className="tw-font-bold tw-text-lg tw-mb-2">Bemanning per station, enhet och skift</h4>
        <p className="tw-text-xs tw-text-gray-500 tw-mb-2">Hel besättning = 2 personer, Halv = 1 person</p>
        <table className="tw-text-xs tw-border-collapse">
          <thead>
            <tr className="tw-bg-gray-200">
              {['Station', 'Enhet', 'Skift', 'Hel Bes.', 'Halv Bes.', 'Hel Vak.', 'Tot. Bes.', 'Tot. Vak.'].map(h => (
                <th key={h} className="tw-border tw-border-gray-400 tw-px-2 tw-py-1 tw-font-bold tw-text-center">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {enheter.map((e, i) => {
              const totals = enhetTotals[`${e.station}|${e.enhetsNummer}`];
              return (
                <tr key={i} className={i % 2 === 0 ? 'tw-bg-white' : 'tw-bg-gray-50'}>
                  {stationSpan[i] > 0 && (
                    <td rowSpan={stationSpan[i]} className={`${cell} tw-font-bold tw-align-middle tw-text-center`}>
                      {e.station}
                    </td>
                  )}
                  {enhetSpan[i] > 0 && (
                    <td rowSpan={enhetSpan[i]} className={`${cell} tw-font-bold tw-align-middle tw-text-center`}>
                      {e.enhetsNummer}
                    </td>
                  )}
                  <td className={cell}>{e.skift}</td>
                  <td className={`${cell} tw-text-center`}>{e.helBesattningBemannad}</td>
                  <td className={`${cell} tw-text-center`}>{e.halvBesattningBemannad}</td>
                  <td className={`${cell} tw-text-center tw-text-red-600 tw-font-bold`}>{e.helBesattningVakant}</td>
                  {enhetSpan[i] > 0 && (
                    <td rowSpan={enhetSpan[i]} className={`${cell} tw-text-center tw-align-middle`}>
                      {totals.totalBesattningar.toFixed(1)}
                    </td>
                  )}
                  {enhetSpan[i] > 0 && (
                    <td rowSpan={enhetSpan[i]} className={`${cell} tw-text-center tw-align-middle`}>
                      {totals.totalVakanser}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="tw-bg-gray-300 tw-font-bold">
              <td colSpan={3} className="tw-border tw-border-gray-400 tw-px-2 tw-py-1">Summa</td>
              <td className="tw-border tw-border-gray-400 tw-px-2 tw-py-1 tw-text-center">{data.totalHelBemannad}</td>
              <td className="tw-border tw-border-gray-400 tw-px-2 tw-py-1 tw-text-center">{data.totalHalvBemannad}</td>
              <td className="tw-border tw-border-gray-400 tw-px-2 tw-py-1 tw-text-center tw-text-red-700">{data.totalHelVakans}</td>
              <td className="tw-border tw-border-gray-400 tw-px-2 tw-py-1 tw-text-center">{data.grandTotalBesattningar?.toFixed(1)}</td>
              <td className="tw-border tw-border-gray-400 tw-px-2 tw-py-1 tw-text-center">{data.grandTotalVakanser}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div>
        <h4 className="tw-font-bold tw-text-lg tw-mb-3">Nyckeltal</h4>
        <div className="tw-grid tw-grid-cols-1 tw-gap-2 tw-min-w-[220px]">
          <Nyckeltal label="Totala produktionstimmar" value={s.totaltMojligaTimmar.toFixed(0)} />
          <Nyckeltal label="Bemannade timmar" value={s.bemannadeTimmarTotal.toFixed(0)} />
          <Nyckeltal label="Vakanta timmar" value={s.vakansTimmarTotal.toFixed(0)} />
          <Nyckeltal label="Bemanningsprocent" value={`${s.matchningsProcent.toFixed(1)} %`} color="tw-bg-green-100 tw-border-green-400" />
          <Nyckeltal label="Vakansprocent" value={`${s.vakansProcent.toFixed(1)} %`} color="tw-bg-red-100 tw-border-red-400" />
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Flik: Vakanser
// ─────────────────────────────────────────────
const VakansTab = ({ data, month }) => {
  const station = data?.station ?? '';
  return (
    <div className="tw-flex tw-gap-8 tw-items-start tw-flex-wrap">
      <h4 className="tw-font-bold tw-text-lg tw-mb-2">Vakanser och bemanning — {station}, {month}</h4>
      <div className="tw-flex tw-gap-8 tw-items-start tw-flex-wrap tw-w-full">
        <LeftTable data={data} month={month} />
        <RightTable data={data} />
      </div>
    </div>
  );
};

const PersonList = ({ persons }) => {
  if (!persons.length) return <span className="tw-text-gray-400 tw-text-xs">—</span>;
  return (
    <ul className="tw-list-none tw-m-0 tw-p-0 tw-text-xs tw-space-y-0.5">
      {persons.map((p, j) => (
        <li key={j} className="tw-whitespace-nowrap">
          <span className="tw-font-semibold tw-text-gray-600">{p.roll}</span>{' '}
          {p.namn}{' '}
          <span className="tw-text-gray-400">({p.station}, {p.tidsIntervall})</span>
        </li>
      ))}
    </ul>
  );
};

const LeftTable = ({ data, month }) => {
  const rows = useMemo(() => {
    const vakanser = data?.vakanser ?? [];
    const blankpassen = data?.blankpassManadsStatistik?.blankpassen ?? [];
    const utbildningarna = data?.utbManadsStatistik?.utbildningarna ?? [];
    const year = parseInt((month ?? '').match(/\d{4}/)?.[0]) || new Date().getFullYear();

    const bpByDatum = {};
    blankpassen.forEach(b => { (bpByDatum[b.datum] ??= []).push(b); });
    const utbByDatum = {};
    utbildningarna.forEach(u => { (utbByDatum[u.datum] ??= []).push(u); });
    const vaksByDatum = {};
    vakanser.forEach(v => { (vaksByDatum[v.datum] ??= []).push(v); });

    const allDates = [...new Set([
      ...vakanser.map(v => v.datum),
      ...blankpassen.map(b => b.datum),
      ...utbildningarna.map(u => u.datum),
    ])].sort();

    if (!allDates.length) return [];

    // Veckoberäkning
    const rowsPerDatum = {};
    allDates.forEach(datum => {
      const dagVaks = vaksByDatum[datum] ?? [];
      rowsPerDatum[datum] = Math.max(dagVaks.length, 1);
    });
    const weekForDatum = {};
    allDates.forEach(datum => {
      const d = parseDatum(datum, year);
      weekForDatum[datum] = d ? getISOWeek(d) : null;
    });
    const veckaRowSpan = {};
    let prevWk = null, wkFirstDatum = null, wkRows = 0;
    for (const datum of allDates) {
      const wk = weekForDatum[datum];
      if (wk !== prevWk) {
        if (wkFirstDatum !== null) veckaRowSpan[wkFirstDatum] = wkRows;
        wkFirstDatum = datum; wkRows = rowsPerDatum[datum]; prevWk = wk;
      } else { wkRows += rowsPerDatum[datum]; }
    }
    if (wkFirstDatum !== null) veckaRowSpan[wkFirstDatum] = wkRows;

    const vakansCount = {};
    vakanser.forEach(v => { const k = `${v.datum}|${v.enhet}|${v.skift}`; vakansCount[k] = (vakansCount[k] || 0) + 1; });

    const cellBorder = 'tw-border tw-border-gray-300 tw-px-3 tw-py-1';
    const veckaCell = `${cellBorder} tw-font-bold tw-text-blue-600 tw-align-middle tw-text-center tw-bg-blue-50 tw-whitespace-nowrap`;

    const builtRows = [];
    for (const datum of allDates) {
      const dagVaks = vaksByDatum[datum] ?? [];
      const bps     = bpByDatum[datum]  ?? [];
      const utbs    = utbByDatum[datum] ?? [];

      if (dagVaks.length === 0) {
        // Datum utan vakanser — en rad, vakanskolumnerna tomma
        builtRows.push(
          <tr key={datum}>
            {veckaRowSpan[datum] != null && (
              <td rowSpan={veckaRowSpan[datum]} className={veckaCell}>v{weekForDatum[datum] ?? '–'}</td>
            )}
            <td className={`${cellBorder} tw-font-bold tw-text-blue-700 tw-align-middle tw-text-center`}>{datum}</td>
            <td className={cellBorder} colSpan={4} />
            <td className={`${cellBorder} tw-align-top`}><PersonList persons={bps} /></td>
            <td className={`${cellBorder} tw-align-top`}><PersonList persons={utbs} /></td>
          </tr>
        );
        continue;
      }

      // Beräkna station- och enhet-span inom detta datums rader
      const stationSpan = new Array(dagVaks.length).fill(0);
      const enhetSpan   = new Array(dagVaks.length).fill(0);
      for (let i = 0; i < dagVaks.length; i++) {
        if (i === 0 || dagVaks[i].station !== dagVaks[i - 1].station) {
          let s = 1;
          while (i + s < dagVaks.length && dagVaks[i + s].station === dagVaks[i].station) s++;
          stationSpan[i] = s;
        }
        if (i === 0 || dagVaks[i].station !== dagVaks[i - 1].station || dagVaks[i].enhet !== dagVaks[i - 1].enhet) {
          let s = 1;
          while (i + s < dagVaks.length
            && dagVaks[i + s].station === dagVaks[i].station
            && dagVaks[i + s].enhet   === dagVaks[i].enhet) s++;
          enhetSpan[i] = s;
        }
      }

      dagVaks.forEach((v, i) => {
        const helVakant = vakansCount[`${v.datum}|${v.enhet}|${v.skift}`] >= 2;
        builtRows.push(
          <tr key={`${datum}-${i}`} className={helVakant ? 'tw-bg-red-100' : 'tw-bg-yellow-50'}>
            {i === 0 && veckaRowSpan[datum] != null && (
              <td rowSpan={veckaRowSpan[datum]} className={veckaCell}>v{weekForDatum[datum] ?? '–'}</td>
            )}
            {i === 0 && (
              <td rowSpan={dagVaks.length} className={`${cellBorder} tw-font-bold tw-text-blue-700 tw-align-middle tw-text-center`}>
                {datum}
              </td>
            )}
            {stationSpan[i] > 0 && (
              <td rowSpan={stationSpan[i]} className={`${cellBorder} tw-font-bold tw-text-blue-700 tw-align-middle tw-text-center`}>
                {v.station}
              </td>
            )}
            {enhetSpan[i] > 0 && (
              <td rowSpan={enhetSpan[i]} className={`${cellBorder} tw-font-bold tw-align-middle tw-text-center`}>
                {v.enhet}
              </td>
            )}
            <td className={cellBorder}>{v.besattning}</td>
            <td className={cellBorder}>{v.skift}</td>
            {i === 0 && (
              <td rowSpan={dagVaks.length} className={`${cellBorder} tw-align-top`}>
                <PersonList persons={bps} />
              </td>
            )}
            {i === 0 && (
              <td rowSpan={dagVaks.length} className={`${cellBorder} tw-align-top`}>
                <PersonList persons={utbs} />
              </td>
            )}
          </tr>
        );
      });
    }

    return builtRows;
  }, [data, month]);

  if (!rows.length)
    return <p className="tw-text-gray-500 tw-text-sm">Inga data för vald period.</p>;

  return (
    <div>
      <h4 className="tw-font-bold tw-text-lg tw-mb-2">Vakanser per datum, station och enhet</h4>
      <div className="tw-flex tw-gap-4 tw-mb-2 tw-text-xs">
        <span className="tw-flex tw-items-center tw-gap-1">
          <span className="tw-w-4 tw-h-4 tw-bg-red-200 tw-border tw-border-red-400 tw-inline-block" />
          Hel besättning vakant
        </span>
        <span className="tw-flex tw-items-center tw-gap-1">
          <span className="tw-w-4 tw-h-4 tw-bg-yellow-50 tw-border tw-border-yellow-400 tw-inline-block" />
          Halv besättning vakant
        </span>
      </div>
      <div className="tw-overflow-x-auto">
        <table className="tw-w-auto tw-text-sm tw-border-collapse">
          <thead>
            <tr className="tw-bg-gray-200">
              <th className="tw-border tw-border-gray-400 tw-px-3 tw-py-2 tw-text-center tw-font-bold tw-whitespace-nowrap">Vecka</th>
              <th className="tw-border tw-border-gray-400 tw-px-3 tw-py-2 Dua gar tw-font-bold tw-whitespace-nowrap">Datum</th>
              <th className="tw-border tw-border-gray-400 tw-px-3 tw-py-2 tw-text-left tw-font-bold tw-whitespace-nowrap">Station</th>
              <th className="tw-border tw-border-gray-400 tw-px-3 tw-py-2 tw-text-left tw-font-bold">Enhet</th>
              <th className="tw-border tw-border-gray-400 tw-px-3 tw-py-2 tw-text-left tw-font-bold tw-whitespace-nowrap">Vakant Bes.</th>
              <th className="tw-border tw-border-gray-400 tw-px-3 tw-py-2 tw-text-left tw-font-bold">Skift</th>
              <th className="tw-border tw-border-gray-400 tw-px-3 tw-py-2 tw-text-left tw-font-bold">Blankpass</th>
              <th className="tw-border tw-border-gray-400 tw-px-3 tw-py-2 tw-text-left tw-font-bold">Utbildning</th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Flik: Diagram
// ─────────────────────────────────────────────
const DiagramTab = ({ data, rollColors }) => {
  const s = data?.manadsStatistik;
  const bp = data?.blankpassManadsStatistik;
  const utb = data?.utbManadsStatistik;
 
  if (!s) return <p className="tw-text-gray-500 tw-text-sm">Ingen statistik.</p>;
  const produktionData = [
    { name: 'Bemannade timmar', value: Number(s.bemannadeTimmarTotal?.toFixed(0) ?? 0) },
    { name: 'Vakanta timmar', value: Number(s.vakansTimmarTotal?.toFixed(0) ?? 0) },
  ];
  const timmarData = [
    { name: 'Totalt möjliga', timmar: Number(s.totaltMojligaTimmar?.toFixed(0) ?? 0), fill: rollColors['AMB'] ?? '#169A42' },
    { name: 'Bemannade', timmar: Number(s.bemannadeTimmarTotal?.toFixed(0) ?? 0), fill: '#eab308' },
    { name: 'Vakanta', timmar: Number(s.vakansTimmarTotal?.toFixed(0) ?? 0), fill: rollColors['VUB'] ?? '#FF0033' },
  ];
  const passData = [
    { name: 'Bemannade', Helpass: data.totalHelBemannad, Halvpass: data.totalHalvBemannad },
    { name: 'Vakanta', Helpass: data.totalHelVakans, Halvpass: data.totalHalvVakans },
  ];
  const rollData = [
    { name: 'AMB', value: Number(s.bemannadeTimmarAMB?.toFixed(0) ?? 0) },
    { name: 'SSK', value: Number(s.bemannadeTimmarSSK?.toFixed(0) ?? 0) },
    { name: 'VUB', value: Number(s.bemannadeTimmarVUB?.toFixed(0) ?? 0) },
    { name: 'LÄK', value: Number(s.bemannadeTimmarLAK?.toFixed(0) ?? 0) },
    { name: 'PSSK', value: Number(s.bemannadeTimmarPSSK?.toFixed(0) ?? 0) },
  ].filter(r => r.value > 0);
  const vakansRollData = [
    { name: 'AMB', value: Number(s.vakansTimmarAMB?.toFixed(0) ?? 0) },
    { name: 'SSK', value: Number(s.vakansTimmarSSK?.toFixed(0) ?? 0) },
    { name: 'VUB', value: Number(s.vakansTimmarVUB?.toFixed(0) ?? 0) },
    { name: 'LÄK', value: Number(s.vakansTimmarLAK?.toFixed(0) ?? 0) },
    { name: 'PSSK', value: Number(s.vakansTimmarPSSK?.toFixed(0) ?? 0) },
  ].filter(r => r.value > 0);
  const bpData = [
    { name: 'AMB', value: Number(bp?.blankpassTimmarAMB?.toFixed(0) ?? 0) },
    { name: 'SSK', value: Number(bp?.blankpassTimmarSSK?.toFixed(0) ?? 0) },
    { name: 'VUB', value: Number(bp?.blankpassTimmarVUB?.toFixed(0) ?? 0) },
    { name: 'LÄK', value: Number(bp?.blankpassTimmarLAK?.toFixed(0) ?? 0) },
    { name: 'PSSK', value: Number(bp?.blankpassTimmarPSSK?.toFixed(0) ?? 0) },
  ].filter(r => r.value > 0);
  const utbData = [
    { name: 'AMB', value: Number(utb?.utbTimmarAMB?.toFixed(0) ?? 0) },
    { name: 'SSK', value: Number(utb?.utbTimmarSSK?.toFixed(0) ?? 0) },
    { name: 'VUB', value: Number(utb?.utbTimmarVUB?.toFixed(0) ?? 0) },
    { name: 'LÄK', value: Number(utb?.utbTimmarLAK?.toFixed(0) ?? 0) },
    { name: 'PSSK', value: Number(utb?.utbTimmarPSSK?.toFixed(0) ?? 0) },
  ].filter(r => r.value > 0);
  return (
    <div className="tw-space-y-8">
      <div className="tw-flex tw-flex-wrap tw-gap-8">
        <div>
          <h5 className="tw-font-bold tw-text-sm tw-mb-4 tw-text-center">Utfall Produktionsdata</h5>
          <PieChart width={350} height={300} margin={{ top: 12, right: 12, bottom: 0, left: 12 }}>
            <Pie data={produktionData} cx={155} cy={110} outerRadius={90} dataKey="value" label={({ percent }) => `${Math.round(percent * 100)}%`}>
              <Cell fill="#0D9488" /><Cell fill="#EAB308" />
            </Pie>
            <Tooltip formatter={v => `${v} tim`} /><Legend />
          </PieChart>
        </div>
        <div>
          <h5 className="tw-font-bold tw-text-sm tw-mb-4 tw-text-center">Bemannade timmar per roll</h5>
          {rollData.length > 0
            ? <PieChart width={350} height={300} margin={{ top: 12, right: 12, bottom: 0, left: 12 }}>
              <Pie data={rollData} cx={155} cy={130} outerRadius={90} dataKey="value" label={({ name, percent }) => `${name} ${Math.round(percent * 100)}%`}>
                {rollData.map(e => <Cell key={e.name} fill={rollColors[e.name]} />)}
              </Pie>
              <Tooltip formatter={v => `${v} tim`} /><Legend />
            </PieChart>
            : <p className="tw-text-gray-400 tw-text-xs tw-text-center tw-mt-10">Ingen data</p>}
        </div>
        <div>
          <h5 className="tw-font-bold tw-text-sm tw-mb-4 tw-text-center">Vakanta timmar per roll</h5>
          {vakansRollData.length > 0
            ? <PieChart width={350} height={300} margin={{ top: 12, right: 12, bottom: 0, left: 12 }}>
              <Pie data={vakansRollData} cx={155} cy={130} outerRadius={90} dataKey="value" label={({ name, percent }) => `${name} ${Math.round(percent * 100)}%`}>
                {vakansRollData.map(e => <Cell key={e.name} fill={rollColors[e.name]} />)}
              </Pie>
              <Tooltip formatter={v => `${v} tim`} /><Legend />
            </PieChart>
            : <p className="tw-text-gray-400 tw-text-xs tw-text-center tw-mt-10">Ingen data</p>}
        </div>
        <div>
          <h5 className="tw-font-bold tw-text-sm tw-mb-4 tw-text-center">Blankpass timmar per roll</h5>
          {bpData.length > 0
            ? <PieChart width={350} height={300} margin={{ top: 12, right: 12, bottom: 0, left: 12 }}>
              <Pie data={bpData} cx={155} cy={110} outerRadius={90} dataKey="value" label={({ name, percent }) => `${name} ${Math.round(percent * 100)}%`}>
                {bpData.map(e => <Cell key={e.name} fill={rollColors[e.name]} />)}
              </Pie>
              <Tooltip formatter={v => `${v} tim`} /><Legend />
            </PieChart>
            : <p className="tw-text-gray-400 tw-text-xs tw-text-center tw-mt-10">Ingen data</p>}
        </div>
        <div>
          <h5 className="tw-font-bold tw-text-sm tw-mb-4 tw-text-center">Utbildningar timmar per roll</h5>
          {utbData.length > 0
            ? <PieChart width={350} height={300} margin={{ top: 12, right: 12, bottom: 0, left: 12 }}>
              <Pie data={utbData} cx={155} cy={110} outerRadius={90} dataKey="value" label={({ name, percent }) => `${name} ${Math.round(percent * 100)}%`}>
                {utbData.map(e => <Cell key={e.name} fill={rollColors[e.name]} />)}
              </Pie>
              <Tooltip formatter={v => `${v} tim`} /><Legend />
            </PieChart>
            : <p className="tw-text-gray-400 tw-text-xs tw-text-center tw-mt-10">Ingen data</p>}
        </div>
      </div>
      <div className="tw-flex tw-flex-wrap tw-gap-8">
        <div>
          <h5 className="tw-font-bold tw-text-sm tw-mb-2 tw-text-center">Utfall Produktionsplanering (timmar)</h5>
          <ResponsiveContainer width={320} height={240}>
            <BarChart data={timmarData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="timmar">{timmarData.map((e, i) => <Cell key={i} fill={e.fill} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div>
          <h5 className="tw-font-bold tw-text-sm tw-mb-2 tw-text-center">Bemannade vs Vakanta Pass</h5>
          <ResponsiveContainer width={320} height={240}>
            <BarChart data={passData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="Helpass" fill={rollColors['AMB'] ?? '#169A42'} /><Bar dataKey="Halvpass" fill="#eab308" />
              <Legend />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Dashboard helpers
// ─────────────────────────────────────────────
const getISOWeek = date => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const y = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - y) / 86400000) + 1) / 7);
};
const parseDatum = (datum, year) => {
  if (!datum) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(datum)) { const [y, m, d] = datum.split('-').map(Number); return new Date(y, m - 1, d); }
  if (/^\d{1,2}\/\d{1,2}$/.test(datum)) { const [d, m] = datum.split('/').map(Number); return new Date(year, m - 1, d); }
  if (/^\d{1,2}-\d{1,2}$/.test(datum)) { const [d, m] = datum.split('-').map(Number); return new Date(year, m - 1, d); }
  return null;
};

// ─────────────────────────────────────────────
// Flik: Dashboard
// ─────────────────────────────────────────────
const DashboardTab = ({ data, month }) => {
  const station = data?.station ?? '';
  const s = data?.manadsStatistik;

  const {
    dailyData, veckaSpan, totalVakanser, totalBesattningar, highestRisk,
    nattPct, criticalDays, weeklyData, maxWeekVakanser, hasVubAmb
  } = useMemo(() => {
    const year = parseInt((month ?? '').match(/\d{4}/)?.[0]) || new Date().getFullYear();
    const vakanser = data?.vakanser ?? [];

    const dailyMap = {};
    vakanser.forEach(v => {
      const key = `${v.datum}|${v.skift}`;
      if (!dailyMap[key]) dailyMap[key] = { datum: v.datum, skift: v.skift, vub: 0, amb: 0, other: 0, total: 0, date: parseDatum(v.datum, year) };
      const e = (v.enhet || '').toUpperCase();
      if (e.includes('VUB')) dailyMap[key].vub++;
      else if (e.includes('AMB')) dailyMap[key].amb++;
      else dailyMap[key].other++;
      dailyMap[key].total++;
    });
    const dailyData = Object.values(dailyMap).sort((a, b) => {
      if (a.date && b.date && a.date.getTime() !== b.date.getTime()) return a.date - b.date;
      return String(a.datum).localeCompare(String(b.datum)) || String(a.skift).localeCompare(String(b.skift));
    });
    dailyData.forEach(row => { row.vecka = row.date ? getISOWeek(row.date) : null; });
    const veckaSpan = new Array(dailyData.length).fill(0);
    for (let i = 0; i < dailyData.length; i++) {
      if (i === 0 || dailyData[i].vecka !== dailyData[i - 1].vecka) {
        let span = 1; while (i + span < dailyData.length && dailyData[i + span].vecka === dailyData[i].vecka) span++;
        veckaSpan[i] = span;
      }
    }
    const totalVakanser = data?.grandTotalVakanser ?? vakanser.length;
    const totalBesattningar = data?.grandTotalBesattningar ?? 0;
    const highestRisk = dailyData.reduce((max, d) => d.total > (max?.total ?? 0) ? d : max, null);
    const nattAntal = vakanser.filter(v => (v.skift || '').toLowerCase().includes('natt')).length;
    const nattPct = vakanser.length > 0 ? Math.round((nattAntal / vakanser.length) * 100) : 0;
    const criticalDays = dailyData.filter(d => d.total >= 4);
    const weeklyMap = {};
    vakanser.forEach(v => {
      const date = parseDatum(v.datum, year); if (!date) return;
      const wk = getISOWeek(date);
      if (!weeklyMap[wk]) weeklyMap[wk] = { week: wk, vakanser: 0 };
      weeklyMap[wk].vakanser++;
    });
    const weeklyData = Object.values(weeklyMap).sort((a, b) => a.week - b.week);
    const maxWeekVakanser = Math.max(...weeklyData.map(w => w.vakanser), 1);
    const hasVubAmb = vakanser.some(v => { const e = (v.enhet || '').toUpperCase(); return e.includes('VUB') || e.includes('AMB'); });

    return {
      dailyData, veckaSpan, totalVakanser, totalBesattningar, highestRisk,
      nattPct, criticalDays, weeklyData, maxWeekVakanser, hasVubAmb
    };
  }, [data, month]);

  const card = 'tw-bg-white tw-rounded-2xl tw-p-5 tw-shadow-sm tw-border tw-border-gray-200';

  return (
    <div className="tw-space-y-5">
      <div className="tw-grid tw-grid-cols-2 xl:tw-grid-cols-4 tw-gap-4">
        <div className={card}>
          <p className="tw-text-xs tw-text-gray-500">Totala vakanser</p>
          <div className="tw-text-3xl tw-font-bold tw-mt-1">{totalVakanser}</div>
          <p className="tw-text-xs tw-text-red-600 tw-mt-1">{data?.totalHelVakans ?? 0} hela · {data?.totalHalvVakans ?? 0} halva</p>
        </div>
        <div className={card}>
          <p className="tw-text-xs tw-text-gray-500">Operativa besättningar</p>
          <div className="tw-text-3xl tw-font-bold tw-mt-1">{typeof totalBesattningar === 'number' ? totalBesattningar.toFixed(0) : totalBesattningar}</div>
          <p className="tw-text-xs tw-text-green-600 tw-mt-1">{s?.matchningsProcent?.toFixed(1) ?? '–'} % bemanning</p>
        </div>
        <div className={card}>
          <p className="tw-text-xs tw-text-gray-500">Mest kritiskt pass</p>
          <div className="tw-text-base tw-font-bold tw-mt-1 tw-leading-snug">{highestRisk ? `${highestRisk.datum} ${highestRisk.skift}` : '–'}</div>
          <p className="tw-text-xs tw-text-amber-600 tw-mt-1">{highestRisk ? `${highestRisk.total} vakanser` : ''}</p>
        </div>
        <div className={card}>
          <p className="tw-text-xs tw-text-gray-500">Nattskiftstryck</p>
          <div className="tw-text-3xl tw-font-bold tw-mt-1">{nattPct} %</div>
          <p className="tw-text-xs tw-text-gray-500 tw-mt-1">av alla vakanser</p>
        </div>
      </div>
      <div className="tw-grid tw-grid-cols-1 xl:tw-grid-cols-3 tw-gap-5">
        <div className={`xl:tw-col-span-2 ${card}`}>
          <h4 className="tw-font-bold tw-text-base tw-mb-3">Daglig vakansöversikt — {station}, {month}</h4>
          <div className="tw-overflow-auto tw-rounded-lg tw-border tw-border-gray-200">
            <table className="tw-w-full tw-text-xs">
              <thead className="tw-bg-gray-100 tw-text-gray-600">
                <tr>
                  <th className="tw-text-center tw-px-3 tw-py-2 tw-whitespace-nowrap">Vecka</th>
                  <th className="tw-text-left tw-px-3 tw-py-2">Datum</th>
                  <th className="tw-text-left tw-px-3 tw-py-2">Skift</th>
                  {hasVubAmb && <><th className="tw-text-center tw-px-3 tw-py-2">VUB</th><th className="tw-text-center tw-px-3 tw-py-2">AMB</th></>}
                  <th className="tw-text-center tw-px-3 tw-py-2">Totalt</th>
                  <th className="tw-text-center tw-px-3 tw-py-2">Risk</th>
                </tr>
              </thead>
              <tbody>
                {dailyData.map((row, i) => (
                  <tr key={i} className="tw-border-t tw-border-gray-100 hover:tw-bg-gray-50">
                    {veckaSpan[i] > 0 && <td rowSpan={veckaSpan[i]} className="tw-px-3 tw-py-1.5 tw-font-bold tw-text-center tw-align-middle tw-text-blue-600 tw-bg-blue-50 tw-border-r tw-border-blue-100 tw-whitespace-nowrap">v{row.vecka ?? '–'}</td>}
                    <td className="tw-px-3 tw-py-1.5 tw-font-medium tw-text-blue-700">{row.datum}</td>
                    <td className="tw-px-3 tw-py-1.5">{row.skift}</td>
                    {hasVubAmb && <><td className="tw-px-3 tw-py-1.5 tw-text-center">{row.vub}</td><td className="tw-px-3 tw-py-1.5 tw-text-center">{row.amb}</td></>}
                    <td className="tw-px-3 tw-py-1.5 tw-text-center tw-font-bold">{row.total}</td>
                    <td className="tw-px-3 tw-py-1.5 tw-text-center">
                      <span className={`tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium ${row.total >= 4 ? 'tw-bg-red-100 tw-text-red-700' : row.total >= 2 ? 'tw-bg-amber-100 tw-text-amber-700' : 'tw-bg-green-100 tw-text-green-700'}`}>
                        {row.total >= 4 ? 'Kritisk' : row.total >= 2 ? 'Förhöjd' : 'Normal'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className={card}>
          <h4 className="tw-font-bold tw-text-base tw-mb-3">Kritiska pass</h4>
          {criticalDays.length === 0
            ? <p className="tw-text-xs tw-text-green-600">Inga kritiska pass denna period.</p>
            : <div className="tw-space-y-2 tw-max-h-64 tw-overflow-y-auto">
              {criticalDays.map((item, idx) => (
                <div key={idx} className="tw-rounded-lg tw-border tw-border-red-200 tw-bg-red-50 tw-p-3">
                  <div className="tw-flex tw-items-center tw-justify-between">
                    <div>
                      <p className="tw-font-semibold tw-text-red-900 tw-text-sm">{item.datum} – {item.skift}</p>
                      <p className="tw-text-xs tw-text-red-700 tw-mt-0.5">{item.total} vakanser</p>
                    </div>
                    <div className="tw-text-2xl tw-font-bold tw-text-red-700">{item.total}</div>
                  </div>
                </div>
              ))}
            </div>}
          <div className="tw-mt-4 tw-rounded-lg tw-bg-gray-50 tw-border tw-border-gray-200 tw-p-3">
            <p className="tw-text-xs tw-text-gray-500 tw-font-bold">Vakansprocent</p>
            <p className="tw-text-2xl tw-font-bold tw-mt-1 tw-text-red-600">{s?.vakansProcent?.toFixed(1) ?? '–'} %</p>
            <div className="tw-w-full tw-bg-gray-200 tw-rounded-full tw-h-2 tw-mt-2 tw-overflow-hidden">
              <div className="tw-bg-red-500 tw-h-2 tw-rounded-full" style={{ width: `${Math.min(s?.vakansProcent ?? 0, 100)}%` }} />
            </div>
            <p className="tw-text-xs tw-text-gray-400 tw-mt-1">Bemanningsprocent: {s?.matchningsProcent?.toFixed(1) ?? '–'} %</p>
          </div>
        </div>
      </div>
      {weeklyData.length > 0 && (
        <div className={card}>
          <h4 className="tw-font-bold tw-text-base tw-mb-4">Veckovis vakansbelastning</h4>
          <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-4">
            {weeklyData.map((wk, idx) => (
              <div key={idx} className="tw-rounded-xl tw-border tw-border-gray-200 tw-p-4 tw-bg-gray-50">
                <div className="tw-flex tw-justify-between tw-items-center">
                  <div><p className="tw-text-xs tw-text-gray-500">Vecka</p><p className="tw-text-2xl tw-font-bold tw-text-gray-900">v{wk.week}</p></div>
                  <div className="tw-text-right"><p className="tw-text-xs tw-text-gray-500">Vakanser</p><p className="tw-text-lg tw-font-bold tw-text-red-700">{wk.vakanser}</p></div>
                </div>
                <div className="tw-mt-3">
                  <div className="tw-w-full tw-bg-gray-200 tw-rounded-full tw-h-2.5 tw-overflow-hidden">
                    <div className="tw-bg-slate-800 tw-h-2.5 tw-rounded-full" style={{ width: `${Math.round((wk.vakanser / maxWeekVakanser) * 100)}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Huvudkomponent
// ─────────────────────────────────────────────
const Statistik = () => {
  const { isAuthorized } = useIsAuthorized();
  const { impersonatedUserId } = useImpersonatedUserId();
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const { monthlyReportNames, setMonthlyReportNames } = useMonthlyReportNames()
  const { userInfo } = useUserInfo();
  const { station } = useStation();
  const [selectedMonth, setSelectedMonth] = useState('');
  const [startDay, setStartDay] = useState('');
  const [endDay, setEndDay] = useState('');
  const [mode, setMode] = useState('range');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [dataLabel, setDataLabel] = useState('');
  const [fetchParams, setFetchParams] = useState(null);
  const [allStationer, setAllStationer] = useState([]);
  const [selectedStationer, setSelectedStationer] = useState([]);
  const [inputChanged, setInputChanged] = useState(false);
  const [rollColors, setRollColors] = useState({});
  const isSystemAdmin = userInfo?.role === 'SystemAdmin';
  const getMonthlyReportNames = async () => {
    try {
      const url = `/api/reports/names?type=manad&station=${encodeURIComponent(station)}`;
      const response = await fetchWithAuth(url);
      if (!response.ok) throw new Error('Kunde inte hämta månadsrapportnamn');
      const data = await response.json();
      if (data.length === 0) {
        setMonthlyReportNames(['Underlag saknas'])
        setSelectedMonth('')
        return 0;
      }
      setMonthlyReportNames(data);
      return data.length;
    } catch (error) {
      console.error(error);
      return 0;
    }
  }

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetchWithAuth('/api/reports/allastationer');
        const d = await response.json();

        const filtered = d.filter(s => s !== '---').sort();

        setAllStationer(filtered);
        setSelectedStationer(filtered);
        await getMonthlyReportNames(); 
        if (isSystemAdmin && impersonatedUserId) {
          const data = await getUserInfoById(impersonatedUserId);
          setImpersonatedUser(data);
        }
      } catch {
        toast.error('Kunde inte ladda stationer');
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    fetchWithAuth('/api/personal/roll')
      .then(r => r.json())
      .then(rolls => {
        const map = {};
        rolls.forEach(r => { map[r.rollNamn] = r.hexFarg; });
        setRollColors(map);
      })
      .catch(() => {});
  }, []);

  const dateOk = mode === 'range' ? !!(startDay && endDay && startDay <= endDay) : !!selectedMonth;
  const canFetch = dateOk && selectedStationer.length > 0;

  const fetchData = async () => {
    if (!canFetch) return;
    let startD, endD, label;
    if (mode === 'range') {
      startD = startDay; endD = endDay; label = `${startDay} – ${endDay}`;
    } else {
      const månader = { Januari: 1, Februari: 2, Mars: 3, April: 4, Maj: 5, Juni: 6, Juli: 7, Augusti: 8, September: 9, Oktober: 10, November: 11, December: 12 };
      const parts = selectedMonth.split(/[_\s]+/);
      const mn = parts[0];
      const mnKey = mn.charAt(0).toUpperCase() + mn.slice(1).toLowerCase();
      const month = månader[mnKey];
      const ys = parts[parts.length - 1];

      const year = parseInt(ys);

      if (!month || isNaN(year)) {
        toast.error(`Kunde inte tolka månaden: ${selectedMonth}`);
        setLoading(false);
        return;
      }

      const end = new Date(year, month, 0);   // sista dagen i månaden
      startD = `${year}-${String(month).padStart(2, '0')}-01`;
      endD = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
      label = `${mn} ${year}`;
    }
    setLoading(true); setData(null);
    try {
      let url = '/api/reports/vacancy-data-station-range';
      const response = await fetchWithAuth(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ startDay: startD, endDay: endD, stationer: selectedStationer })
      });
      if (!response.ok) throw new Error('Kunde inte hämta statistik');
      setData(await response.json());
      setDataLabel(label);
      setFetchParams({ startD, endD, stationer: selectedStationer });
      setInputChanged(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    if (!data || !fetchParams) return;
    setExcelLoading(true);
    try {
      let url = '/api/reports/vacancy-excel-station-range';
      if (impersonatedUserId) url += `?userId=${impersonatedUserId}`;
      const response = await fetchWithAuth(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ startDay: fetchParams.startD, endDay: fetchParams.endD, stationer: fetchParams.stationer })
      });
      if (!response.ok) throw new Error('Kunde inte generera Excel-rapport');
      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'Vakanser.xlsx';
      if (contentDisposition) {
        const m = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (m?.[1]) filename = m[1].replace(/['"]/g, '');
      }
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExcelLoading(false);
    }
  };

  if (!isAuthorized) return null;

  const tabs = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'oversikt', label: 'Vakanser & Bemanning' },
    { id: 'diagram', label: 'Diagram' },
  ];

  const inputCls = 'tw-border tw-border-gray-300 tw-rounded tw-p-2 tw-text-sm tw-font-bold';

  // Använd lokal tid — new Date("yyyy-MM-dd") tolkas som UTC midnatt vilket ger fel datum i UTC+2.
  const toDateObj = (str) => {
    if (!str) return null;
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d); // lokal midnatt
  };
  const toDateStr = (date) => {
    if (!date) return '';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  return (
    <div className="tw-px-4 sm:tw-px-6 lg:tw-px-8">
      <div className="tw-max-w-7xl tw-mx-auto tw-py-3">
        <div className="tw-rounded-3xl tw-overflow-hidden tw-border tw-border-blue-500">

          <div className="tw-bg-gradient-to-r tw-from-blue-500 tw-to-blue-600 tw-text-white tw-px-4 tw-py-3">
            <h3 className="tw-m-0 tw-text-2xl tw-font-bold tw-text-center">Bemanning & Vakanser</h3>
          </div>
          {impersonatedUserId && impersonatedUser && (
            <div className="tw-px-6 tw-text-left">
              <h3 className="tw-font-semibold tw-text-lg tw-text-gray-800">
                Du agerar som användare:
                <span className="tw-text-red-500 hover:tw-text-red-700 tw-ml-1">
                  <strong>{impersonatedUser.userName}</strong>
                </span>
              </h3>
            </div>
          )}
          <div className={
            impersonatedUserId
              ? 'tw-px-6 tw-pb-6'
              : 'tw-p-6'
          }
          >
            {/* ── Indata ─────────────────────────────────────── */}
            <div className="tw-mb-6 tw-pb-5 tw-border-b tw-border-gray-200 tw-space-y-5">
              {/* Steg 1: Period */}
              <div>
                <p className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-2">1. Välj period</p>
                <div className="tw-flex tw-gap-0 tw-mb-3 tw-w-fit tw-rounded tw-border tw-border-gray-300 tw-overflow-hidden">
                  {[['range', 'Dagintervall'], ['month', 'Månad']].map(([id, label]) => (
                    <button key={id} onClick={() => { setMode(id); setData(null); setSelectedMonth(''); }}
                      className={`tw-px-5 tw-py-2 tw-text-sm tw-font-bold tw-transition-colors ${mode === id ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50'}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <div className="tw-flex tw-items-end tw-gap-3 tw-flex-wrap">
                  {mode === 'range' ? (
                    <>
                      <DatePicker
                        selected={toDateObj(startDay)}
                        onChange={date => {const str = toDateStr(date); setStartDay(str); setInputChanged(true);}}
                        locale="sv"
                        dateFormat="yyyy-MM-dd"
                        calendarStartDay={1}
                        showWeekNumbers
                        className={inputCls}
                        placeholderText="Från datum"
                      />
                      <span className="tw-text-gray-400 tw-pb-2">–</span>
                      <DatePicker
                        selected={toDateObj(endDay)}
                        onChange={date => { setEndDay(toDateStr(date)); setInputChanged(true); }}
                        locale="sv"
                        dateFormat="yyyy-MM-dd"
                        calendarStartDay={1}
                        showWeekNumbers
                        className={inputCls}
                        placeholderText="Till datum"
                      />
                    </>
                  ) : (
                    <select value={selectedMonth} onChange={e => { setSelectedMonth(e.target.value); setInputChanged(true); }} className={`${inputCls} tw-min-w-[200px]`}>
                      <option value="">Välj månad</option>
                      {monthlyReportNames
                        .filter(m => m !== 'Underlag saknas')
                        .map((m, i) => (
                          <option key={`${m}-${i}`} value={m}>{m.replace('_', ' ')}</option>
                        ))}
                    </select>
                  )}
                </div>
              </div>

              {/* Steg 2: Stationer */}
              <div>
                <p className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-2">2. Välj stationer</p>
                <StationValjare
                  allStationer={allStationer}
                  value={selectedStationer}
                  onChange={(s) => { setSelectedStationer(s); setInputChanged(true); }}
                />
              </div>

              {/* Steg 3: Hämta */}
              <div className="tw-flex tw-items-center tw-gap-3">
                <button onClick={fetchData} disabled={!canFetch || loading}
                  className="tw-bg-blue-600 hover:tw-bg-blue-700 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed tw-text-white tw-text-sm tw-font-bold tw-px-6 tw-py-2 tw-rounded tw-transition-opacity">
                  {loading ? 'Hämtar...' : 'Hämta produktionsdata'}
                </button>
                <button onClick={downloadExcel} disabled={!data || inputChanged || excelLoading}
                  className="tw-bg-green-700 hover:tw-bg-green-800 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed tw-text-white tw-text-sm tw-font-bold tw-px-6 tw-py-2 tw-rounded tw-transition-opacity">
                  {excelLoading ? 'Genererar...' : 'Excel'}
                </button>
                {!canFetch && !loading && (
                  <span className="tw-text-xs tw-text-gray-400">
                    {!dateOk ? 'Välj period först' : 'Välj minst en station'}
                  </span>
                )}
                {loading && <BattenburgLoader top={-100} />}
              </div>
            </div>

            {/* ── Tabs ─────────────────────────────────────────── */}
            <div className="tw-flex tw-border-b tw-border-gray-300 tw-mb-4">
              {tabs.map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`tw-px-6 tw-py-2 tw-text-sm tw-font-bold tw-border-b-2 tw-transition-colors ${activeTab === tab.id ? 'tw-border-blue-500 tw-text-blue-600' : 'tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700'}`}>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ── Innehåll ─────────────────────────────────────── */}
            {data ? (
              <>
                {activeTab === 'dashboard' && <DashboardTab data={data} month={dataLabel} />}
                {activeTab === 'oversikt' && <VakansTab data={data} month={dataLabel} />}
                {activeTab === 'diagram' && <DiagramTab data={data} rollColors={rollColors} />}
              </>
            ) : (
              <div className="tw-py-12 tw-text-center">
                <p className="tw-text-gray-400 tw-text-sm">Välj period och stationer ovan, klicka sedan Hämta produktionsdata.</p>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistik;
